import { useTranslation } from 'react-i18next';
import { LimitUsagePeriods, Skeleton, withStyles } from 'vopay';
import { BodyText, Card, Title } from 'UI';
import { LIMIT_PERIODS, LimitAmounts, LimitPeriod, toLimitAmount } from './accountLimits';
import styles from './TransactionLimitsCard.module.scss';

const cn = withStyles(styles);

const NOT_AVAILABLE = '—';

type TransactionLimitsCardProps = {
  currency: string;
  /** Configured limits per period; undefined while loading, null when the details could not be loaded. */
  limits: LimitAmounts | null | undefined;
  /**
   * Amounts transacted to date per period. Null when the backend did not price them for this
   * account (see `LimitUsageAvailable`), undefined while loading.
   */
  usage: LimitUsagePeriods | null | undefined;
};

/**
 * The limits card (spec 5.5): Daily, Weekly and Monthly used against the outbound limit configured
 * for each. Outbound is the direction the design's "Outbound Global Velocities" limits are defined
 * for; the endpoint returns inbound as well, which nothing renders.
 *
 * The figures carry the comparison on their own, so there is no usage bar — the design has none,
 * and a bar beside the rules that separate the rows only ever read as a second rule. Shared by the
 * details drawer and the full account page.
 */
export default function TransactionLimitsCard({ currency, limits, usage }: TransactionLimitsCardProps) {
  const { t, i18n } = useTranslation('translations', {
    keyPrefix: 'Accounts.AccountHeaderCards.TransactionLimitsCard',
  });

  const periodLabels: Record<LimitPeriod, string> = {
    Daily: t('daily', { defaultValue: 'Daily' }),
    Weekly: t('weekly', { defaultValue: 'Weekly' }),
    Monthly: t('monthly', { defaultValue: 'Monthly' }),
  };

  // Figma shows the figures without cents ("5,200 / 30,000"); a fractional limit would still keep its.
  const formatFigure = (amount: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  const isLoading = limits === undefined || usage === undefined;

  return (
    <Card variant="section" className={cn('card')} data-testid="transactionLimitsCard">
      <div className={cn('header')}>
        <Title level="6" className={cn('cardTitle')}>
          {t('title', { defaultValue: 'Transaction Limits' })}
        </Title>
      </div>

      <div className={cn('rows')}>
        {LIMIT_PERIODS.map((period) => {
          const limit = limits?.[period] ?? null;
          const used = usage ? toLimitAmount(usage[period]) : null;

          const limitText = () => {
            // No details at all is a different state from "no limit configured".
            if (limits === null) return NOT_AVAILABLE;
            if (limit === null) return t('noLimit', { defaultValue: '/ No limit' });

            return t('limitOf', { defaultValue: '/ {{limit}}', limit: formatFigure(limit) });
          };

          return (
            <div className={cn('row')} key={period} data-testid={`limitRow-${period}`}>
              <span className={cn('periodLabel')}>{periodLabels[period]}</span>

              {isLoading ? (
                <Skeleton width={160} height={24} />
              ) : (
                <div className={cn('amounts')}>
                  <BodyText size="sm" weight="em">
                    {currency}
                  </BodyText>

                  <BodyText weight="em" data-testid="limitUsed">
                    {used === null ? NOT_AVAILABLE : formatFigure(used)}
                  </BodyText>

                  <BodyText className={cn('limitOf')} data-testid="limitOf">
                    {limitText()}
                  </BodyText>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
